import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listFrequencies } from "../services/teacher";
import { listClassrooms } from "../services/classroom";
import { getReportCardYear, printReportCardYear } from "../services/reportcard";
import { isCambridgeSucursal } from "../services/auth";
import { levelKey } from "../utils/classesLabels";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const TERM_LABELS = { term1: "1º Trimestre", term2: "2º Trimestre", term3: "3º Trimestre" };

const round1 = (v) => (v === null || v === undefined ? "-" : Math.round(v * 10) / 10);
const fmtCat = (cell) => (cell && cell.percent !== null && cell.percent !== undefined ? `${Math.round(cell.percent)}% (${cell.letter})` : "-");

// Novo ecrã, só faz sentido para sucursais com o modelo Cambridge activo
// (ver src/services/auth.js#isCambridgeSucursal). Reaproveita os endpoints
// já existentes em sge_infantil_core (routes/reportcard.js) — getReportCardYear
// devolve os 3 trimestres lado a lado, printReportCardYear gera o PDF (URL S3).
const BoletimLayer = () => {
  const { t } = useTranslation();
  const isCambridge = isCambridgeSucursal();

  const [year, setYear] = useState(currentYear);
  const [frequencies, setFrequencies] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [classrooms, setClassrooms] = useState([]);
  const [classId, setClassId] = useState("");

  const [reportCard, setReportCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    if (!isCambridge) return;
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, [isCambridge]);

  useEffect(() => {
    if (!isCambridge || !levelId) {
      setClassrooms([]);
      return;
    }
    listClassrooms(year).then((resp) => setClassrooms((resp.data || []).filter((c) => c.educationLevel === levelId))).catch(() => setClassrooms([]));
  }, [isCambridge, levelId, year]);

  useEffect(() => {
    setClassId("");
    setReportCard(null);
  }, [levelId, year]);

  useEffect(() => {
    if (!isCambridge || !levelId || !classId) {
      setReportCard(null);
      return;
    }
    setLoading(true);
    getReportCardYear({ levelId, classId, year })
      .then((resp) => setReportCard(resp.data))
      .catch(() => toast.error(t("boletim.error.load")))
      .finally(() => setLoading(false));
  }, [isCambridge, levelId, classId, year, t]);

  const lastTermKey = reportCard?.lastTermWithData || "term1";

  const handlePrint = async (studentId) => {
    setPrintingId(studentId);
    try {
      const resp = await printReportCardYear({ levelId, classId, year, studentId });
      if (resp.data?.url) window.open(resp.data.url, "_blank", "noopener,noreferrer");
      else toast.error(t("boletim.error.print"));
    } catch (err) {
      toast.error(err?.response?.data?.error || t("boletim.error.print"));
    } finally {
      setPrintingId(null);
    }
  };

  const sortedStudents = useMemo(
    () => [...(reportCard?.students || [])].sort((a, b) => (a.student.name || "").localeCompare(b.student.name || "", "pt")),
    [reportCard],
  );

  if (!isCambridge) {
    return (
      <div className='row gy-4'>
        <div className='col-12'>
          <div className='alert alert-info mb-0'>{t("boletim.hint.notCambridge")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='p-16 radius-8 bg-neutral-50'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                  <select className='form-control form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-4'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("turma.field.educationLevel"))}</label>
                  <select className='form-control form-select' value={levelId} onChange={(e) => setLevelId(e.target.value)}>
                    <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("turma.col.className")}</label>
                  <select className='form-control form-select' value={classId} onChange={(e) => setClassId(e.target.value)} disabled={!levelId}>
                    <option value=''>{t("pauta.field.class.placeholder")}</option>
                    {classrooms.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.className}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!classId ? (
        <div className='col-12'>
          <div className='alert alert-info mb-0'>{t(levelKey("boletim.hint.selectClass"))}</div>
        </div>
      ) : (
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body'>
              {loading ? (
                <div className='text-center py-40'>{t("global.loading")}</div>
              ) : sortedStudents.length === 0 ? (
                <div className='alert alert-info mb-0'>{t("boletim.hint.noStudents")}</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }} />
                        <th>{t("presenca.col.name")}</th>
                        <th>{t("boletim.col.exams")}</th>
                        <th>{t("boletim.col.progress")}</th>
                        <th>{t("boletim.col.coursework")}</th>
                        <th>{t("boletim.col.behaviour")}</th>
                        <th>{t("boletim.col.total")}</th>
                        <th className='text-end'>{t("turma.col.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedStudents.map((s) => {
                        const summary = s.termSummaries[lastTermKey] || {};
                        const isExpanded = expandedStudentId === s.student.id;
                        return (
                          <React.Fragment key={s.student.id}>
                            <tr role='button' className={isExpanded ? "bg-neutral-50" : ""} onClick={() => setExpandedStudentId(isExpanded ? null : s.student.id)}>
                              <td>
                                <Icon icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"} className='text-secondary-light text-xl' />
                              </td>
                              <td className='fw-semibold text-primary-light'>{s.student.name}</td>
                              <td>{round1(summary.categoryAverages?.exams)}%</td>
                              <td>{round1(summary.categoryAverages?.progressTest)}%</td>
                              <td>{round1(summary.categoryAverages?.courseWork)}%</td>
                              <td>{fmtCat(summary.behaviour)}</td>
                              <td className='fw-semibold'>{summary.total ? `${round1(summary.total.percent)}% (${summary.total.letter})` : "-"}</td>
                              <td className='text-end' onClick={(e) => e.stopPropagation()}>
                                <button
                                  type='button'
                                  className='btn btn-outline-success-600 btn-sm d-flex align-items-center gap-2 ms-auto'
                                  disabled={printingId === s.student.id}
                                  onClick={() => handlePrint(s.student.id)}
                                >
                                  <Icon icon={printingId === s.student.id ? "line-md:loading-twotone-loop" : "mdi:file-pdf-box"} />
                                  {printingId === s.student.id ? t("global.loading") : t("boletim.action.printPdf")}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={8} className='p-0'>
                                  <div className='p-24 bg-neutral-25 border-top'>
                                    <div className='table-responsive mb-16'>
                                      <table className='table bordered-table table-sm mb-0 bg-base'>
                                        <thead>
                                          <tr>
                                            <th>{t("disciplina.field.subjectName")}</th>
                                            {["term1", "term2", "term3"].map((tk) => (
                                              <React.Fragment key={tk}>
                                                <th colSpan={3} className='text-center'>{TERM_LABELS[tk]}</th>
                                              </React.Fragment>
                                            ))}
                                          </tr>
                                          <tr>
                                            <th />
                                            {["term1", "term2", "term3"].map((tk) => (
                                              <React.Fragment key={tk}>
                                                <th>{t("boletim.col.exams")}</th>
                                                <th>{t("boletim.col.progress")}</th>
                                                <th>{t("boletim.col.coursework")}</th>
                                              </React.Fragment>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {s.subjects.map((subj) => (
                                            <tr key={subj.subjectId}>
                                              <td className='fw-semibold'>{subj.name}</td>
                                              {["term1", "term2", "term3"].map((tk) => (
                                                <React.Fragment key={tk}>
                                                  <td>{fmtCat(subj.terms[tk]?.exam)}</td>
                                                  <td>{fmtCat(subj.terms[tk]?.progress)}</td>
                                                  <td>{fmtCat(subj.terms[tk]?.coursework)}</td>
                                                </React.Fragment>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    {summary.teacherComment && (
                                      <p className='text-secondary-light text-sm mb-0'>
                                        <strong>{t("boletim.col.teacherComment")}:</strong> {summary.teacherComment}
                                      </p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoletimLayer;
